import React, { useState, useEffect } from "react";
import { Box, Button, Typography, styled } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { ContactSupportDialog } from "./ContactSupportDialog";
import { getUserFacilities } from "@services/facilityManagement";
import { getCurrentUserId } from "@utils/userUtils";
import {
  getUserPreferredFacilities,
  UserPreferredFacility,
} from "@services/payment";
import { readFacilityCart } from "@utils/multiFacilityCart";

/* ── Styled components (matching Profile page CSS) ── */

const PageContainer = styled(Box)({
  background: "#fdffff",
  borderRadius: "12px",
  height: "97vh",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #99d4e1",
  boxShadow: "8px 2px 58.4px 0px inset rgba(191, 242, 255, 0.2)",
  position: "relative",
  overflow: "hidden",
});

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  padding: "12px 64px",
  height: "124px",
  display: "flex",
  alignItems: "end",
  gap: "8px",
  position: "sticky",
  top: 0,
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    padding: "12px 24px",
    height: "96px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px",
    height: "80px",
  },
}));

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "28px",
  fontWeight: 500,
  lineHeight: "1.5",
  color: "#262626",
  letterSpacing: "-1px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "22px",
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: "32px 48px 16px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  overflowY: "auto",
  overflowX: "hidden",
  [theme.breakpoints.down("md")]: {
    padding: "24px 24px 16px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 16px",
  },
}));

/* ── Component ── */

interface VerificationInProgressPageProps {
  /** Called when user clicks OK – parent can navigate away */
  onOk?: () => void;
}

const VerificationInProgressPage: React.FC<VerificationInProgressPageProps> = ({
  onOk,
}) => {
  const location = useLocation();
  const routeState = location.state as {
    selectedFacility?: {
      facility_name?: string;
      city?: string;
      state?: string;
      ccn_number?: string;
      facility_type?: string;
    };
  };
  const [contactSupportOpen, setContactSupportOpen] = useState(false);
  const [hasExistingFacilities, setHasExistingFacilities] = useState(false);
  const [pendingFacilities, setPendingFacilities] = useState<
    UserPreferredFacility[]
  >([]);
  const [, setActiveFacilities] = useState<
    UserPreferredFacility[]
  >([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userId = getCurrentUserId();
        if (userId === "0") return;
        const numericUserId = parseInt(userId);

        // Always fetch user facilities to check for existing facilities
        const userFacilitiesRes = await getUserFacilities(numericUserId);
        const userFacs = userFacilitiesRes.facilities || [];
        setHasExistingFacilities(userFacs.length > 0);

        // If selectedFacility is passed via navigation state, use only that
        if (routeState?.selectedFacility) {
          const selected = routeState.selectedFacility;
          // Create a mock UserPreferredFacility object from the selected facility
          const mockFacility: UserPreferredFacility = {
            id: 0, // Temporary ID
            user_id: numericUserId,
            facility_details: {
              facility_name: selected.facility_name || "",
              city: selected.city || "",
              state: selected.state || "",
              ccn_number: selected.ccn_number || "",
              facility_type: selected.facility_type || "",
            },
            facility_name: selected.facility_name || "",
            city: selected.city || "",
            state: selected.state || "",
            ccn_number: selected.ccn_number || "",
            facility_type: selected.facility_type || "",
            is_verified: false,
            is_active: true,
            is_delete: false,
          } as UserPreferredFacility;
          setPendingFacilities([mockFacility]);
        } else {
          // Otherwise, fetch trial pending only — exclude multi-cart checkout rows.
          const preferredRes = await getUserPreferredFacilities(1, 20, numericUserId);
          const preferred =
            preferredRes.result?.data?.filter((f) => !f.is_delete) || [];
          const cartIds = new Set(
            readFacilityCart()
              .map((f) => Number(f.id))
              .filter((id) => Number.isFinite(id) && id > 0),
          );

          setHasExistingFacilities(userFacs.length > 0 || preferred.length > 0);
          setPendingFacilities(
            preferred.filter(
              (f) =>
                f.is_active &&
                !f.is_verified &&
                !cartIds.has(Number(f.id)),
            ),
          );
          setActiveFacilities(
            preferred.filter((f) => f.is_active && f.is_verified)
          );
        }
      } catch {
        // ignore
      }
    };
    fetchData();
  }, [routeState?.selectedFacility]);

  return (
    <PageContainer>
      {/* ── Header ── */}
      <HeaderContainer>
        <HeaderTitle>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "999px",
              backgroundColor: "#E0F6FB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InfoOutlined sx={{ fontSize: 22, color: "#0A829D" }} />
          </Box>
          <Title>Verification in Progress</Title>
        </HeaderTitle>
      </HeaderContainer>

      {/* ── Content ── */}
      <ContentSection>
        <Box sx={{ maxWidth: 720, width: "100%" }}>
          {/* Pending facilities list */}
          {pendingFacilities.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#065B70",
                  mb: 1.5,
                }}
              >
                Selected Facility 
              </Typography>
              {pendingFacilities.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    px: 2,
                    mb: 1,
                    borderRadius: "8px",
                    border: "1px solid #e5e5e5",
                    background: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#F59E0B",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "14px",
                      color: "#262626",
                    }}
                  >
                    {f.facility_name ||
                      f.facility_details?.facility_name ||
                      "Unknown Facility"}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Rethink Sans", sans-serif',
                      fontSize: "12px",
                      color: "#F59E0B",
                      ml: "auto",
                    }}
                  >
                    Verification pending
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {/* Main message card */}
          <Box
            sx={{
              background: "#F0FDFF",
              border: "1px solid #BFE5EC",
              borderRadius: "12px",
              p: 4,
              mt: 3,
            }}
          >
          <Typography
            sx={{
              color: "#262626",
              fontSize: "15px",
              fontFamily: '"Rethink Sans", sans-serif',
              lineHeight: 1.7,
              mb: 2,
            }}
          >
            We are verifying your account and the selected facility. This
            process may take up to 24 hours. You'll receive an email once
            verification is complete with instructions on next steps.
          </Typography>

          {hasExistingFacilities && (
            <Typography
              sx={{
                color: "#4b5563",
                fontSize: "13px",
                fontFamily: '"Rethink Sans", sans-serif',
                lineHeight: 1.6,
              }}
            >
              You can continue using the platform for your existing facilities
              while verification is in progress.
            </Typography>
          )}
        </Box>

          {/* Action buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
            }}
          >
            <Button
            onClick={() => setContactSupportOpen(true)}
            sx={{
              background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
              boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
              borderRadius: "8px",
              padding: "10px 24px",
              fontFamily: '"Rethink Sans", sans-serif',
              fontWeight: 600,
              fontSize: "14px",
              color: "#e6fafc",
              textTransform: "none",
              "&:hover": {
                background:
                  "linear-gradient(90deg, #086e86 0%, #4ab3c7 100%)",
              },
            }}
          >
            Contact Support
          </Button>
          </Box>
        </Box>
      </ContentSection>

      {/* Contact Support Dialog */}
      <ContactSupportDialog
        open={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
        onSuccess={() => {
          // Show toast in top-right corner, stays visible even after modal closes
          toast.success("Your message or your query has been sent successfully.", {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }}
      />
    </PageContainer>
  );
};

export default VerificationInProgressPage;

